<script lang="ts" setup>
	import { useProductsApi, type BenefitGroupedData } from '~/services/apis/products'
	const props = defineProps<{
		productCode: number
		productCategory: string
		provider: ProductProviders | ''
		groupId?: string
		show: boolean
		desc?: string
	}>()

	const { productCategoryType, toLower } = useUtils()

	const { getProductBenefit, getProductClaimProcess, getProductNeedToKnow, getProductWorks, getProductInformation } =
		useProductsApi()
	const categoryType = computed(() => productCategoryType(props.productCategory))
	type Content = BenefitGroupedData[string]
	type steps = 'benefits' | 'needToKnow' | 'howItWorks' | 'howToClaim'
	const benefits = ref<Record<steps, { label: string; key: steps; list: Content; disabled?: boolean }>>({
		benefits: {
			label: 'Benefits',
			key: 'benefits',
			list: []
		},
		needToKnow: {
			label: 'Need To Know',
			key: 'needToKnow',
			list: []
		},
		howItWorks: {
			label: 'How It Works',
			key: 'howItWorks',
			list: [] as Content
		},
		howToClaim: {
			label: 'How To Claim',
			key: 'howToClaim',
			disabled: categoryType.value.isHealth || categoryType.value?.isTravel,
			list: []
		}
	})
	const step = shallowRef<steps>('benefits')
	const currentBenefit = computed(() => benefits.value[step.value])
	const activeBenefit = shallowRef<string | null>(null)
	defineEmits<{
		(event: 'update:show', param: boolean): void
	}>()
	const loadingBenefit = shallowRef(false)
	const fetchBenefit = () => {
		if (!props.show) return
		benefits.value.howToClaim.disabled = categoryType.value.isHealth || categoryType.value?.isTravel
		if (props.productCategory) {
			resolve(async () => {
				const options = {
					group_id: props.groupId,
					code_name: props.productCode,
					provider: props.provider,
					category: props.productCategory,
					user_type: UserTypes.BUSINESS
				}
				const [benefit, information] = await Promise.allSettled([
					getProductBenefit({ ...options, params: props.productCode }),
					getProductInformation(options)
				])
				if (benefit.status === 'fulfilled' && information.status === 'fulfilled') {
					benefits.value.benefits.list = benefit.value as Content
					const informationData = information.value?.data
					if (informationData) {
						benefits.value.needToKnow.list = informationData.need_to_know as Content
						benefits.value.howItWorks.list = informationData.how_it_works as Content
						benefits.value.howToClaim.list = (informationData?.claim_process || []) as Content
					}
				}
			}, loadingBenefit)
		}
	}

	watch(() => props.show, fetchBenefit, { immediate: true })
</script>

<template>
	<div>
		<dialogs
			:show="show"
			btn-position="center"
			modal-class="max-w-[700px] !px-0"
			@change="() => $emit('update:show', false)"
		>
			<div key="content" class="">
				<div v-show="desc" class="desc lg:text-18 px-3 py-3 sm:mt-4 md:px-5" v-html="desc || ''" />

				<div class="w-full">
					<div class="scrollable-x flex w-full items-center px-5 py-5">
						<template v-for="(item, i) in benefits" :key="'labels' + i">
							<button
								v-if="!item.disabled"
								type="button"
								:class="step === item.key ? 'bg-primary-2' : 'bg-transparent text-gray-600'"
								class="default-btn btn-sm !h-10 min-w-[100px] flex-none justify-center !py-1 !text-[14px] capitalize"
								@click="() => (step = item.key)"
							>
								{{ item.label }}
							</button>
						</template>
					</div>

					<div
						class="md:px- pt- main-content-box relative max-h-[700px] space-y-3 overflow-y-auto px-3 pb-4 md:px-5"
					>
						<loader-skeleton
							v-if="loadingBenefit"
							:count="5"
							:height="50"
							class="w-full"
							:max-width="false"
						/>
						<template v-else>
							<template v-for="(item, i) in currentBenefit.list" :key="'content' + i">
								<div
									v-if="(item.provider === provider || !categoryType.isHealth) && step === 'benefits'"
								>
									<div
										class="sticky left-0 top-0 inline-flex w-full items-center justify-between rounded-md bg-primary-50 p-4 font-medium capitalize"
									>
										<span class="font-semibold text-gray-600">
											<span v-if="typeof item.title === 'string'">{{ item.title }}</span>
											<span v-else>{{ item.title.text }}</span>
										</span>
										<button
											type="button"
											aria-label="toggle"
											:aria-expanded="
												activeBenefit === `${item.title}-${i}` ||
												(i == 0 && activeBenefit === null)
											"
											:style="{
												transform:
													activeBenefit === `${item.title}-${i}` ||
													(i == 0 && activeBenefit === null)
														? 'rotate(180deg)'
														: 'rotate(0deg)'
											}"
											class="custom-transition-2"
											@click="
												() =>
													(activeBenefit =
														activeBenefit === `${item.title}-${i}`
															? ''
															: `${item.title}-${i}`)
											"
										>
											<tools-icon dir="arrows" icon="caret-down-r" />
										</button>
									</div>

									<div
										class="custom-transition-2 overflow-hidden overflow-y-auto"
										:style="{
											maxHeight:
												activeBenefit === `${item.title}-${i}` ||
												(i == 0 && activeBenefit === null)
													? '400px'
													: '0'
										}"
									>
										<div
											v-if="!item.list?.length"
											class="border-b-[2px] border-dotted border-gray-200 px-5 py-5"
										>
											{{ item.description?.text }}
										</div>
										<template v-for="(list, listIndex) in item.list" v-else :key="listIndex">
											<div
												v-if="
													!['n/a', 'nil', ''].includes(String(list.label?.text).toLowerCase())
												"
												class="border-b-[2px] border-dotted border-gray-200"
											>
												<div
													class="flex items-start justify-between gap-x-1 pt-5 tracking-wide"
												>
													<div class="flex-none pt-1">
														<lazy-tools-icon
															:icon="
																Boolean(list.description?.excluded)
																	? 'minus-circle'
																	: 'check'
															"
															:dir="Boolean(list.description?.excluded) ? 'line' : 'fill'"
															:height="13"
															:width="13"
														/>
													</div>
													<div class="flex w-full items-center justify-between">
														<div class="text-sm font-medium text-gray-700">
															{{ list.label?.text ?? '---' }}
														</div>
													</div>
												</div>
												<!-- Collection -->
												<ul v-if="list.collection?.length" class="m-0 px-5 py-1">
													<li
														v-for="(collection, collectionIndex) in list.collection"
														:key="collectionIndex"
														class="list-item w-full text-xs tracking-wider text-gray-600"
													>
														<div>{{ collection?.text ?? '' }}</div>
													</li>
												</ul>
												<div
													v-else
													class="list-item w-full px-5 py-1 text-xs tracking-wider text-gray-600"
												>
													<div>{{ list.description?.text || '' }}</div>
												</div>
											</div>
										</template>
									</div>
								</div>

								<div
									v-else-if="typeof item?.title != 'string' && item?.title?.text"
									class="w-full text-gray-600"
								>
									<div class="flex items-center gap-3 text-sm font-medium capitalize">
										<lazy-tools-icon icon="check" dir="fill" />
										<span class="text-gray-600">{{ item?.title?.text ?? '' }}</span>
									</div>
								</div>
							</template>
						</template>
					</div>
				</div>
			</div>
		</dialogs>
	</div>
</template>

<style scoped>
	.desc {
		background-color: #ebe9fe;
	}

	.scrollable-y {
		overflow-y: scroll;
	}

	.th {
		background-color: #ebe9fe;
		color: var(--gray-700);
	}

	.th:first-child {
		background-color: #f9fafb;
		color: var(--success);
	}

	.td {
		background-color: #fafbfd;
		padding: 1rem;
	}

	.td:first-child {
		background-color: #fff;
	}

	.table {
		overflow: auto;
		max-height: 65vh;
	}

	.table::-webkit-scrollbar {
		width: 2px;
	}

	.thead {
		position: sticky;
		top: 0;
		left: 0;
		width: 100%;
	}

	.benefit-container li {
		list-style: disc;
		padding: 0.5rem;
	}

	.label {
		position: sticky;
		top: 0;
		left: 0;
		width: 100%;
		background-color: #fff;
		padding-bottom: 0.3rem;
	}

	.line-h-25 {
		line-height: 25px;
	}
	.scrollable-x {
		overflow-x: scroll !important;
	}
	.close-container {
		z-index: 2;
		width: 100vw;
		min-height: 100vh;
		height: 100%;
	}

	.content {
		background: #ffffff;
		box-shadow: 0 4px 27px rgba(0, 0, 0, 0.08);
		width: 97vw;
		max-width: 700px;
		height: 80vh;
		max-height: 686px;
		position: absolute;
		top: 50%;
		left: 50%;
		overflow: hidden;
		overflow-y: auto;
		transform: translate(-50%, -50%);
		z-index: 3;
	}
</style>
